// "Send RFID tag" dashboard quick action (Session AEA). The Director is out on site and the
// office needs to get a lift's RFID URL to him fast -- previously that meant opening the lift,
// scrolling to the bottom and finding the tag block. This modal is a PICKER over the register:
// search for the lift (or care equipment), and each row carries the real RfidTagButton -- the
// ONE tag mechanism (status, URL + QR, generate, send to an engineer by email or WhatsApp,
// admin rotate/remove). Nothing about the send path or the message is forked here, and this is
// deliberately NOT a page: the retired standalone /office/nfc register stays retired (locked
// decision in RfidTagButton's header); the Lifts list keeps its scan-only seven columns.
//
// Session AMZ: generalised over the lift's TWO tags by `tagKind`, the same prop RfidTagButton takes,
// for the same reason. Nothing here knows what either tag IS: it picks the asset and hands the row
// to the one tag control, so the internal attendance tag inherits the identical picker, search and
// Escape handling without a second file. `tagKind` defaults to "rfid", so the dashboard's existing
// quick action is unchanged.
import { useEffect, useMemo, useState } from "react";
import { assets as assetsResource, type Asset } from "../../lib/resources";
import { useDebouncedValue } from "../../lib/useDebouncedValue";
import { ModalShell } from "../../components/ModalShell";
import { RfidTagButton } from "./register/RfidTagButton";
import type { TagKind } from "../../lib/rfid";

// The picker's own copy per tag. The tap variant never says "sticker": that word belongs to the
// customer-facing tag in the car, and this one lives beside the log card in the motor room.
const PICKER_COPY: Record<TagKind, { title: string; intro: string }> = {
  rfid: {
    title: "Send RFID tag",
    intro: "Pick the lift or equipment, then manage its tag: copy the URL, show the QR or send it to an engineer.",
  },
  tap: {
    title: "Send Guv POP Tag",
    intro:
      "Pick the lift, then manage its internal POP Tag: copy the URL or send it to an engineer to fix beside the motor room log card.",
  },
};

const RFID_PICKER_TAKE = 50;

export function SendRfidTagModal({ onClose, tagKind = "rfid" }: { onClose: () => void; tagKind?: TagKind }) {
  const copy = PICKER_COPY[tagKind];
  const [items, setItems] = useState<Asset[] | null>(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  // Which rows currently have their tag modal open (review finding): every open ModalShell has
  // its own window Escape listener, so an UNGUARDED close here would tear down the whole stack
  // (picker + tag modal + send modal) on one Escape. Guarding on the child set makes Escape
  // close ONE level, the platform convention; RfidTagButton reports via onOpenChange (and
  // reports a still-open modal closed on unmount, so this can never wedge).
  const [openTagIds, setOpenTagIds] = useState<ReadonlySet<string>>(new Set());
  const tagOpen = openTagIds.size > 0;
  const onTagOpenChange = (id: string, open: boolean) =>
    setOpenTagIds((s) => {
      const next = new Set(s);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });

  // Session SC2A: the register is SEARCHED (up to 50 matches, retired items left out), never loaded whole.
  const debouncedQ = useDebouncedValue(q.trim(), 250);
  useEffect(() => {
    let live = true;
    assetsResource
      .list({ take: String(RFID_PICKER_TAKE), includeRetired: "false", ...(debouncedQ ? { q: debouncedQ } : {}) })
      .then((list) => {
        if (live) {
          setItems(list);
          setError(false);
        }
      })
      .catch(() => {
        if (live) setError(true);
      });
    return () => {
      live = false;
    };
  }, [debouncedQ]);

  // Active assets only (a retired hoist has no tag to send, and the search leaves retired items out), lifts before
  // equipment, then by ELE reference. The server search covers the reference, serial, site, postcode, customer and type.
  const filtered = useMemo(() => {
    if (!items) return [];
    const ref = (a: Asset) => a.eleNumber ?? a.legacyEleNumber ?? "";
    return items
      .filter((a) => !a.retiredAt)
      .sort(
        (a, b) =>
          (a.assetClass === "lift" ? 0 : 1) - (b.assetClass === "lift" ? 0 : 1) ||
          ref(a).localeCompare(ref(b)),
      );
  }, [items]);

  return (
    <ModalShell
      ariaLabel={copy.title}
      onClose={() => !tagOpen && onClose()}
      width="max-w-[560px]"
      panelClassName="max-h-[85vh] overflow-y-auto p-6 text-left"
    >
      <div className="mb-1 flex items-start justify-between gap-3">
        <h2 className="font-heading text-xl text-ele-strong">{copy.title}</h2>
        <button onClick={onClose} aria-label="Close" className="text-ele-body hover:text-ele-strong">
          ✕
        </button>
      </div>
      <p className="mb-3 text-sm text-ele-body">{copy.intro}</p>

      <input
        type="search"
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by ELE number, site, customer or type"
        aria-label="Search the register"
        className="mb-3 w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-2 text-sm text-ele-strong placeholder:text-ele-body outline-none focus:border-ele-green"
      />

      {items === null && !error ? (
        <p className="text-sm text-ele-body">Loading the register...</p>
      ) : error ? (
        <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          Could not load the lifts. Please try again.
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-ele-body opacity-70">
          {q.trim() ? "Nothing on the register matches that search." : "Nothing on the register yet."}
        </p>
      ) : (
        <ul className="space-y-2">
          {filtered.map((a) => (
            <li
              key={a.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2"
            >
              <div className="min-w-0">
                <p className="font-heading text-sm text-ele-strong">{a.eleNumber ?? a.legacyEleNumber ?? "Unnumbered"}</p>
                <p className="truncate text-xs text-ele-body">
                  {a.site?.name ?? "No site"}
                  {a.site?.customer?.name ? ` | ${a.site.customer.name}` : ""}
                </p>
              </div>
              <RfidTagButton
                asset={a}
                kind={a.assetClass === "lift" ? "lift" : "equipment"}
                tagKind={tagKind}
                onOpenChange={(open) => onTagOpenChange(a.id, open)}
              />
            </li>
          ))}
        </ul>
      )}
      {items && items.length >= RFID_PICKER_TAKE && (
        <p className="mt-2 text-xs text-ele-body opacity-70">Showing the first {RFID_PICKER_TAKE} matches. Type more of the reference, site or customer to narrow the list.</p>
      )}
    </ModalShell>
  );
}
